import * as React from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { getAiWorkflowRun } from '../../Api';
import { AiWorkflowRunDetailDto } from '../../models/dtos/AiDto';
import AiWorkflowRunView from '../../components/ai/AiWorkflowRunView';
import { aiLoadErrorText, CopyButton } from '../../components/ai/AiShared';

/** AI › Workflows › one run: everything a workflow instance did, by its run key (the caller's correlation id). */
export default function AiWorkflowRunPage() {
    const { code = '', runKey = '' } = useParams();
    const [run, setRun] = React.useState<AiWorkflowRunDetailDto | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [failed, setFailed] = React.useState(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        const result = await getAiWorkflowRun(code, runKey);
        setRun(result);
        setFailed(result == null);
        setLoading(false);
    }, [code, runKey]);

    React.useEffect(() => { load(); }, [load]);

    return (
        <Stack gap={2} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" alignItems="center" gap={1}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h4">{run?.name ?? code} run</Typography>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <Typography variant="body2" color="text.secondary">Correlation id</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{runKey}</Typography>
                        <CopyButton text={runKey} />
                    </Stack>
                </Box>
                <Button component={RouterLink} to={`/ai/workflows/${encodeURIComponent(code)}`}>Workflow</Button>
                <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
            </Stack>
            {loading && !run && <CircularProgress />}
            {failed && <Alert severity="error">{aiLoadErrorText('ai-workflow-run', 'No calls with this run key.')}</Alert>}
            {run && <AiWorkflowRunView run={run} />}
        </Stack>
    );
}
